// eslint-disable-next-line import/no-extraneous-dependencies

import { AuthState } from '@okta/okta-auth-js';
import {
  CdkStore,
  DmsAuthSession,
  IAMUser,
  useDMSAuth
} from 'drive-ui-packages';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import TagManager from 'react-gtm-module';
import { APP_ENV } from 'adapters/constants';
import { GTMDataLayer } from 'adapters/types';
import { useClient } from 'components/ClientProvider';
import { DropdownOption } from 'components/Dropdown/types';
import { defaultGtmDataLayer, gtmId } from 'hooks/constants';
import { useFlagValue } from './useFlags';

const isDataLayerReady = (dataLayer: GTMDataLayer): boolean =>
  dataLayer.d_user_id !== '' &&
  dataLayer.d_company_id !== '' &&
  dataLayer.d_company_name !== '' &&
  dataLayer.d_company_access !== '' &&
  dataLayer.d_enterprise_id !== '' &&
  dataLayer.d_department_id !== '' &&
  dataLayer.d_user_roles.length > 0;

const areDataLayersEqual = (
  dataLayer1: GTMDataLayer,
  dataLayer2: GTMDataLayer
): boolean =>
  _.isEqual(dataLayer1.d_user_id, dataLayer2.d_user_id) &&
  _.isEqual(dataLayer1.d_company_id, dataLayer2.d_company_id) &&
  _.isEqual(dataLayer1.d_company_name, dataLayer2.d_company_name) &&
  _.isEqual(dataLayer1.d_company_access, dataLayer2.d_company_access) &&
  _.isEqual(dataLayer1.d_enterprise_id, dataLayer2.d_enterprise_id) &&
  _.isEqual(dataLayer1.d_department_id, dataLayer2.d_department_id) &&
  _.isEqual(dataLayer1.d_user_roles, dataLayer2.d_user_roles);

export const useInitializeGoogleTagManager = (
  existingStores: CdkStore[] | undefined,
  selectedStore: DropdownOption
): void => {
  const client = useClient();
  const [localGtmDataLayer, setLocalGtmDataLayer] =
    useState<GTMDataLayer>(defaultGtmDataLayer);
  const [initializedGtmDataLayer, setInitializedGtmDataLayer] =
    useState<GTMDataLayer>(defaultGtmDataLayer);
  const allowSoldStoresFlag = useFlagValue(
    'dmsAccountingAllowSoldStoresInUserDetails'
  );
  const enableJwtFlag = useFlagValue('dmsModAccountingJwtTokenEnable');
  const flagOffValue = allowSoldStoresFlag === undefined ? null : undefined;
  const {
    dmsAuthSession
  }: { authState: AuthState | null; dmsAuthSession: DmsAuthSession } =
    useDMSAuth(
      APP_ENV,
      allowSoldStoresFlag ? ['OPEN', 'SOLD'] : flagOffValue,
      enableJwtFlag ?? null
    );

  const user = dmsAuthSession?.oktaUser?.iamUser as IAMUser;

  const clientUserName = user?.username;

  const clientEnterpriseId = user?.enterprise?.id;
  const clientUserRoles = user?.assignments[0]?.roles.map(role => role.code);
  const clientCompanyId = selectedStore?.value;
  const clientCompanyName = selectedStore?.label;
  const clientCompanyAccess =
    existingStores?.length && existingStores?.length > 1
      ? 'multiple'
      : 'singular';
  const clientDepartmentId = client?.customClientValues?.departmentIds?.find(
    store => store.storeId === selectedStore?.value
  )?.departmentIds;

  useEffect(() => {
    if (clientUserName && localGtmDataLayer.d_user_id !== clientUserName) {
      setLocalGtmDataLayer({
        ...localGtmDataLayer,
        d_user_id: clientUserName
      });
    }

    if (
      clientEnterpriseId &&
      localGtmDataLayer.d_enterprise_id !== clientEnterpriseId
    ) {
      setLocalGtmDataLayer({
        ...localGtmDataLayer,
        d_enterprise_id: clientEnterpriseId
      });
    }

    if (clientCompanyId && localGtmDataLayer.d_company_id !== clientCompanyId) {
      setLocalGtmDataLayer({
        ...localGtmDataLayer,
        d_company_id: clientCompanyId
      });
    }

    if (
      clientCompanyName &&
      localGtmDataLayer.d_company_name !== clientCompanyName
    ) {
      setLocalGtmDataLayer({
        ...localGtmDataLayer,
        d_company_name: clientCompanyName
      });
    }

    if (
      clientUserRoles &&
      !_.isEqual(localGtmDataLayer.d_user_roles, clientUserRoles)
    ) {
      setLocalGtmDataLayer({
        ...localGtmDataLayer,
        d_user_roles: clientUserRoles
      });
    }

    if (
      clientCompanyAccess &&
      localGtmDataLayer.d_company_access !== clientCompanyAccess
    ) {
      setLocalGtmDataLayer({
        ...localGtmDataLayer,
        d_company_access: clientCompanyAccess
      });
    }

    if (
      clientDepartmentId &&
      !_.isEqual(localGtmDataLayer.d_department_id, clientDepartmentId)
    ) {
      setLocalGtmDataLayer({
        ...localGtmDataLayer,
        d_department_id: clientDepartmentId
      });
    }
  }, [
    clientCompanyAccess,
    clientCompanyId,
    clientCompanyName,
    clientDepartmentId,
    clientEnterpriseId,
    clientUserName,
    clientUserRoles,
    localGtmDataLayer
  ]);

  useEffect(() => {
    if (!window.dataLayer && isDataLayerReady(localGtmDataLayer)) {
      TagManager.initialize({
        gtmId,
        dataLayer: localGtmDataLayer
      });
      setInitializedGtmDataLayer(localGtmDataLayer);
    }
  }, [localGtmDataLayer]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (
        window.dataLayer &&
        isDataLayerReady(initializedGtmDataLayer) &&
        !areDataLayersEqual(localGtmDataLayer, initializedGtmDataLayer)
      ) {
        TagManager.initialize({
          gtmId,
          dataLayer: localGtmDataLayer
        });
        setInitializedGtmDataLayer(localGtmDataLayer);
      }
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [localGtmDataLayer, initializedGtmDataLayer]);
};
